import React, { useState } from 'react';
import { Button } from '@components/primitives/Button';
import { useAppointments } from '@features/appointments';
import { SettingsIcon } from '@jotpsych/ui/icons/SettingsIcon';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import type { WeekStartDay } from '../store/slice';

interface CalendarSettingsMenuProps {
  variant?: 'icon' | 'fullWidth';
}

type CalendarStep = 1 | 5 | 10 | 15;
type CalendarTimeslots = 1 | 2 | 3 | 4 | 6 | 12;

const ROW_DURATION_OPTIONS = [1, 5, 10, 15, 20, 30, 60, 120] as const;
type RowDurationOption = (typeof ROW_DURATION_OPTIONS)[number];

const ROW_DURATION_MARKS = ROW_DURATION_OPTIONS.map((duration, index) => ({
  value: index,
  label: duration === 60 ? '1h' : duration === 120 ? '2h' : `${duration}m`,
}));

const WEEK_START_OPTIONS: ReadonlyArray<{ value: WeekStartDay; label: string }> = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

// Hour-of-day options. Start picker covers 0-23 (midnight to 11 PM); end picker
// covers 1-24 (1 AM to midnight) so users can select a full-day range and the
// end is always strictly after the start.
const formatHourLabel = (hour: number) => {
  if (hour === 0 || hour === 24) return '12:00 AM';
  if (hour === 12) return '12:00 PM';
  const suffix = hour < 12 ? 'AM' : 'PM';
  const display = hour < 12 ? hour : hour - 12;
  return `${display}:00 ${suffix}`;
};

const START_HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: formatHourLabel(i),
}));

const END_HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: i + 1,
  label: formatHourLabel(i + 1),
}));

// Type guard for valid timeslot values
const isValidTimeslot = (value: number): value is CalendarTimeslots => {
  return [1, 2, 3, 4, 6, 12].includes(value);
};

// Utility function to get closest valid row duration
const getClosestValidDuration = (duration: number): number => {
  return ROW_DURATION_OPTIONS.reduce((prev, curr) =>
    Math.abs(curr - duration) < Math.abs(prev - duration) ? curr : prev,
  );
};

const isRowDurationOption = (value: number): value is RowDurationOption =>
  ROW_DURATION_OPTIONS.includes(value as RowDurationOption);

const getRowDurationIndex = (duration: number): number => {
  const rowDuration = isRowDurationOption(duration)
    ? duration
    : getClosestValidDuration(duration);
  return ROW_DURATION_OPTIONS.indexOf(rowDuration as RowDurationOption);
};

const getCalendarGranularityForRowDuration = (
  rowDuration: RowDurationOption,
  currentStep: CalendarStep,
): { step: CalendarStep; timeslots: CalendarTimeslots } => {
  const currentTimeslots = rowDuration / currentStep;

  if (Number.isInteger(currentTimeslots) && isValidTimeslot(currentTimeslots)) {
    return { step: currentStep, timeslots: currentTimeslots };
  }

  switch (rowDuration) {
    case 1:
      return { step: 1, timeslots: 1 };
    case 5:
      return { step: 5, timeslots: 1 };
    case 10:
      return { step: 10, timeslots: 1 };
    case 15:
      return { step: 5, timeslots: 3 };
    case 20:
      return { step: 5, timeslots: 4 };
    case 30:
      return { step: 5, timeslots: 6 };
    case 60:
      return { step: 5, timeslots: 12 };
    case 120:
      return { step: 10, timeslots: 12 };
    default:
      return { step: 15, timeslots: 4 };
  }
};

const CalendarSettingsMenu: React.FC<CalendarSettingsMenuProps> = ({ variant = 'icon' }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const {
    calendarGranularity,
    setCalendarGranularity,
    weekStartDay,
    setWeekStartDay,
    calendarVisibleHours,
    setCalendarVisibleHours,
  } = useAppointments();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Calculate the current row duration based on step and timeslots
  const currentRowDuration = calendarGranularity.step * calendarGranularity.timeslots;

  const handleRowDurationChange = (_event: Event, value: number | number[]) => {
    const optionIndex = Array.isArray(value) ? value[0] : value;
    const rowDuration = ROW_DURATION_OPTIONS[optionIndex];

    if (!rowDuration) return;
    setCalendarGranularity(
      getCalendarGranularityForRowDuration(rowDuration, calendarGranularity.step),
    );
  };

  const handleStepChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStep = parseInt(event.target.value, 10) as CalendarStep;
    const currentRowDuration = calendarGranularity.step * calendarGranularity.timeslots;
    const targetRowDuration = getClosestValidDuration(currentRowDuration) as RowDurationOption;

    setCalendarGranularity(getCalendarGranularityForRowDuration(targetRowDuration, newStep));
  };

  const handleWeekStartChange = (event: { target: { value: unknown } }) => {
    const next = Number(event.target.value);
    if ([0, 1, 2, 3, 4, 5, 6].includes(next)) {
      setWeekStartDay(next as WeekStartDay);
    }
  };

  const handleStartHourChange = (event: { target: { value: unknown } }) => {
    const nextStart = Number(event.target.value);
    if (!Number.isFinite(nextStart)) return;
    // Keep the end strictly after the new start; bump end up if needed.
    const nextEnd =
      calendarVisibleHours.endHour > nextStart
        ? calendarVisibleHours.endHour
        : Math.min(24, nextStart + 1);
    setCalendarVisibleHours({ startHour: nextStart, endHour: nextEnd });
  };

  const handleEndHourChange = (event: { target: { value: unknown } }) => {
    const nextEnd = Number(event.target.value);
    if (!Number.isFinite(nextEnd)) return;
    const nextStart =
      calendarVisibleHours.startHour < nextEnd
        ? calendarVisibleHours.startHour
        : Math.max(0, nextEnd - 1);
    setCalendarVisibleHours({ startHour: nextStart, endHour: nextEnd });
  };

  return (
    <>
      {variant === 'fullWidth' ? (
        <Button
          variant="outlined"
          onClick={handleClick}
          startIcon={<SettingsIcon />}
          fullWidth
          sx={{
            color: '#535976',
            borderColor: 'rgba(0, 13, 77, 0.45)',
            justifyContent: 'flex-start',
            px: 2,
            py: 1,
            '&:hover': {
              borderColor: '#535976',
              backgroundColor: 'rgba(83, 89, 118, 0.04)',
            },
          }}
        >
          Calendar Settings
        </Button>
      ) : (
        <Tooltip title="Settings" placement="bottom" arrow={false}>
          <IconButton
            onClick={handleClick}
            size="small"
            aria-label="Calendar settings"
            sx={{
              color: '#535976',
              '&:hover': {
                backgroundColor: 'rgba(83, 89, 118, 0.08)',
              },
            }}
          >
            <SettingsIcon fontSize="medium" />
          </IconButton>
        </Tooltip>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPaper-root': {
            minWidth: 240,
            mt: 1,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Settings
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Time slot rows
          </Typography>
          <Slider
            aria-label="Time slot row zoom"
            value={getRowDurationIndex(currentRowDuration)}
            onChange={handleRowDurationChange}
            min={0}
            max={ROW_DURATION_OPTIONS.length - 1}
            step={null}
            marks={ROW_DURATION_MARKS}
            valueLabelDisplay="auto"
            valueLabelFormat={value => {
              const duration = ROW_DURATION_OPTIONS[value];
              if (duration === 60) return '1 hour';
              if (duration === 120) return '2 hours';
              return `${duration} minutes`;
            }}
            sx={{
              mx: 1,
              mt: 2,
              mb: 3,
              width: 'calc(100% - 16px)',
              '& .MuiSlider-markLabel': {
                fontSize: 11,
              },
            }}
          />
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Drag precision
          </Typography>
          <RadioGroup value={calendarGranularity.step} onChange={handleStepChange}>
            <FormControlLabel
              value={1}
              control={<Radio size="small" />}
              label="1 minute"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
            <FormControlLabel
              value={5}
              control={<Radio size="small" />}
              label="5 minutes"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
            <FormControlLabel
              value={10}
              control={<Radio size="small" />}
              label="10 minutes"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
            <FormControlLabel
              value={15}
              control={<Radio size="small" />}
              label="15 minutes"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          </RadioGroup>
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Week starts on
          </Typography>
          <FormControl size="small" fullWidth>
            <InputLabel id="calendar-week-start-label">First day</InputLabel>
            <Select
              labelId="calendar-week-start-label"
              label="First day"
              value={weekStartDay}
              onChange={handleWeekStartChange}
              inputProps={{ 'aria-label': 'Week start day' }}
            >
              {WEEK_START_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Visible hours
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel id="calendar-visible-start-label">Start</InputLabel>
              <Select
                labelId="calendar-visible-start-label"
                label="Start"
                value={calendarVisibleHours.startHour}
                onChange={handleStartHourChange}
                inputProps={{ 'aria-label': 'Visible hours start' }}
              >
                {START_HOUR_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="calendar-visible-end-label">End</InputLabel>
              <Select
                labelId="calendar-visible-end-label"
                label="End"
                value={calendarVisibleHours.endHour}
                onChange={handleEndHourChange}
                inputProps={{ 'aria-label': 'Visible hours end' }}
              >
                {END_HOUR_OPTIONS.map(option => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    disabled={option.value <= calendarVisibleHours.startHour}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      </Menu>
    </>
  );
};

export default CalendarSettingsMenu;
